import { message } from "antd";
import { isAbortError } from "./isAbortError";

const DEFAULT_FALLBACK_MESSAGE = "Something went wrong. Please try again later.";
const SESSION_EXPIRED_MESSAGE = "Session expired, please login again.";
const RATE_LIMIT_MESSAGE = "Too many requests, try again later.";
const VALIDATION_FALLBACK_MESSAGE = "Please check your input and try again.";
const NOT_ENOUGH_SEATS_MESSAGE =
  "Not enough seats available in this group to add the selected member(s). Please increase the group's seat limit or remove existing members first.";

const isValidationError = (status) => status >= 400 && status < 500;

const isNetworkError = (error) => {
  if (!error) return false;
  if (!error.response) return true;
  return ["ECONNABORTED", "ERR_NETWORK"].includes(error.code);
};

const extractMessage = (error) => {
  return (
    error?.response?.data?.details ||
    error?.response?.data?.message ||
    error?.message
  );
};

const isAuthStatus = (status) => status === 401 || status === 403;
const isRateLimitStatus = (status) => status === 429;
const isServerStatus = (status) => typeof status === "number" && status >= 500;
const isValidationStatus = (status) => status === 400 || status === 422;

// Heuristic: allow short, non-technical validation text to reach users.
const looksUserActionable = (msg) => {
  if (typeof msg !== "string") return false;
  const s = msg.trim();
  if (!s) return false;
  if (s.length > 180) return false;
  // Block common technical/internal indicators.
  const technical = /(stack|trace|exception|sql|database|postgres|mysql|oracle|redis|sequelize|typeorm|prisma|null pointer|undefined|ECONN|ENOTFOUND|timeout|500|internal server)/i;
  return !technical.test(s);
};

export const getUserFacingErrorMessage = (error, options = {}) => {
  const status = error?.response?.status;
  const fallbackMessage = options.fallbackMessage || DEFAULT_FALLBACK_MESSAGE;
  const derived = extractMessage(error);

  if (isAuthStatus(status)) return SESSION_EXPIRED_MESSAGE;
  if (isRateLimitStatus(status)) return RATE_LIMIT_MESSAGE;

  if (isNetworkError(error) || isServerStatus(status)) return fallbackMessage;

  if (isValidationStatus(status)) {
    if (error?.response?.data?.not_enough_seats) return NOT_ENOUGH_SEATS_MESSAGE;
    if (looksUserActionable(derived)) return derived.trim();
    return VALIDATION_FALLBACK_MESSAGE;
  }

  // For other 4xx, keep it user-safe and actionable.
  if (isValidationError(status)) {
    if (error?.response?.data?.not_enough_seats) return NOT_ENOUGH_SEATS_MESSAGE;
    if (looksUserActionable(derived)) return derived.trim();
    return VALIDATION_FALLBACK_MESSAGE;
  }

  // Unknown shape: default safe fallback.
  return fallbackMessage;
};

export const handleError = (error, options = {}) => {
  if (isAbortError(error)) {
    return {
      aborted: true,
      context: options.context || "aborted request",
      status: null,
      message: "",
      userMessage: "",
      raw: error,
    };
  }

  const {
    context = "Unknown error context",
    fallbackMessage = DEFAULT_FALLBACK_MESSAGE,
    showToast,
    toastDuration = 3,
  } = options;

  const status = error?.response?.status;
  const userMessage = getUserFacingErrorMessage(error, { fallbackMessage });
  const derivedMessage = extractMessage(error) || fallbackMessage;
  const payload = {
    context,
    status: status ?? null,
    message: derivedMessage,
    userMessage,
    raw: error,
  };

  if (isNetworkError(error)) {
    console.error("[Network Error]", payload);
  } else if (isValidationError(status)) {
    console.warn("[Validation Error]", payload);
  } else {
    console.error("[Application Error]", payload);
  }

  // Golden Rule default: show only safe, actionable messages to users.
  const shouldToast =
    typeof showToast === "boolean"
      ? showToast
      : isNetworkError(error) ||
        isServerStatus(status) ||
        isAuthStatus(status) ||
        isRateLimitStatus(status) ||
        isValidationStatus(status);

  if (shouldToast) {
    message.error({
      content: userMessage,
      duration: toastDuration,
    });
  }

  return payload;
};

export default handleError;
